import {useRouter} from 'next/router';
import {useMemo} from 'react';

/** Monolith subscription checkout reads this query key on express checkout */
export const COUPON_QUERY_PARAM = 'coupon';

/** Some marketing links use this alternate key, we accept it as a fallback on the LP only */
export const COUPON_CODE_QUERY_PARAM = 'couponCode';

const COUPON_QUERY_KEYS = [COUPON_QUERY_PARAM, COUPON_CODE_QUERY_PARAM] as const;

/** Parse a coupon code from a URL search string  */
export function getCouponCodeFromSearch(search: string): string | undefined {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    for (const key of COUPON_QUERY_KEYS) {
        const value = params.get(key);
        if (value && value.length > 0) {
            return value;
        }
    }
    return undefined;
}

function getCouponCodeFromRouterQuery(
    query: Record<string, string | string[] | undefined>,
): string | undefined {
    for (const key of COUPON_QUERY_KEYS) {
        const raw = query[key];
        if (raw === undefined) {
            continue;
        }
        const value = Array.isArray(raw) ? raw[0] : raw;
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
    }
    return undefined;
}

/** SSR coupon detection when `window` is unavailable */
export function hasCouponInUrlFromRouter(
    query: Record<string, string | string[] | undefined>,
    isReady: boolean | undefined,
): boolean {
    if (!isReady) {
        return false;
    }
    return Boolean(getCouponCodeFromRouterQuery(query));
}

/**
 * `true` when the current URL includes a subscription coupon query param.
 * Reads `window.location` on the client so coupon links are detected before Next router hydration.
 */
export function useHasCouponInUrl(): boolean {
    const router = useRouter();
    return useMemo(() => {
        if (typeof window !== 'undefined') {
            return Boolean(getCouponCodeFromSearch(window.location.search));
        }
        return hasCouponInUrlFromRouter(router.query, router?.isReady);
    }, [router?.isReady, router?.query]);
}

/**
 * Coupon code from the current page URL (`coupon`, then `couponCode`).
 * On the client, reads `window.location.search` first so the initial plan pricing fetch can include
 * the coupon before `router.isReady`.
 */
export function useCouponCodeParam(): string | undefined {
    const router = useRouter();

    if (typeof window !== 'undefined') {
        const fromWindow = getCouponCodeFromSearch(window.location.search);
        if (fromWindow) {
            return fromWindow;
        }
    }

    if (!router?.isReady) {
        return undefined;
    }

    return getCouponCodeFromRouterQuery(router.query);
}
